type DateString = string | null | undefined | Date;
type RealmLocale = { language: string; country: string; timezone?: string };
export const ONE_DAY = 60 * 60 * 24;

function localeFromRealm(realm: RealmLocale) {
    return realm.language.length > 2
        ? realm.language.replace("_", "-")
        : realm.language + "-" + realm.country;
}

const DATE_ONLY = /^\d{4}(-\d{2}){0,2}$/;

function resolveOptions(
    input: DateString,
    realm: RealmLocale,
    options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormatOptions {
    const timeZone =
        typeof input === "string" && DATE_ONLY.test(input)
            ? "UTC"
            : realm.timezone;
    return { timeZone, ...options };
}

export function formatDate(
    input: DateString,
    realm: RealmLocale,
    options: Intl.DateTimeFormatOptions,
): string {
    if (!input) return "";
    const date = input instanceof Date ? input : new Date(input);
    if (isNaN(date.getTime())) return "";
    const resolved = resolveOptions(input, realm, options);

    try {
        return new Intl.DateTimeFormat(localeFromRealm(realm), resolved).format(
            date,
        );
    } catch {
        return new Intl.DateTimeFormat(localeFromRealm(realm), {
            ...resolved,
            timeZone: "GMT",
        }).format(date);
    }
}

export function formatRange(
    dateString1: DateString,
    dateString2: DateString,
    realm: RealmLocale,
    options: Intl.DateTimeFormatOptions = {
        day: "numeric",
        month: "short",
    },
) {
    if (!dateString1 || !dateString2) return "";
    const date1 = new Date(dateString1);
    const date2 = new Date(dateString2);
    if (isNaN(date1.getTime()) || isNaN(date2.getTime())) return "";
    const resolved = resolveOptions(dateString1, realm, options);

    try {
        return new Intl.DateTimeFormat(
            localeFromRealm(realm),
            resolved,
        ).formatRange(date1, date2);
    } catch {
        return new Intl.DateTimeFormat(localeFromRealm(realm), {
            ...resolved,
            timeZone: "GMT",
        }).formatRange(date1, date2);
    }
}

export function hasDateTimePassed(dateTimeString: DateString) {
    if (!dateTimeString) return false;

    return new Date() > new Date(dateTimeString);
}

// Plural names satisfy both Intl.RelativeTimeFormat (accepts either form)
// and Intl.DurationFormat (accepts plural keys only)
type TimeUnit = "years" | "months" | "days" | "hours" | "minutes" | "seconds";

const units: [TimeUnit, number][] = [
    ["years", 365 * 24 * 60 * 60 * 1000],
    ["months", 30 * 24 * 60 * 60 * 1000],
    ["days", 24 * 60 * 60 * 1000],
    ["hours", 60 * 60 * 1000],
    ["minutes", 60 * 1000],
    ["seconds", 1000],
];

function getUnitFromDiff(diff: number) {
    const unitToUse =
        units.find(([_, duration]) => duration <= Math.abs(diff)) ||
        units[units.length - 1];
    return unitToUse;
}

export function formatRelative(dateString: DateString, realm: RealmLocale) {
    if (!dateString) return "";
    const date = new Date(dateString);
    const diff = date.getTime() - Date.now();
    const [unitName, unitAmount] = getUnitFromDiff(diff);
    const formatter = new Intl.RelativeTimeFormat(localeFromRealm(realm), {
        numeric: "auto",
    });
    return formatter.format(Math.round(diff / unitAmount), unitName);
}

export function formatDuration(dateString: DateString, realm: RealmLocale) {
    if (!dateString) return "";
    const date = new Date(dateString);
    const diff = Math.abs(date.getTime() - Date.now());
    const [unitName, unitAmount] = getUnitFromDiff(diff);
    const formatter = new Intl.DurationFormat(localeFromRealm(realm), {
        style: "long",
    });
    return formatter.format({ [unitName]: Math.round(diff / unitAmount) });
}

export function isWithinDays(dateString: DateString, days: number) {
    if (!dateString) return true;
    const date = new Date(dateString);
    const diff = Date.now() - date.getTime();
    return diff <= days * ONE_DAY * 1000;
}

export const LONG_DATE: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric",
};

export const SHORT_DATE: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
};

export const SHORT_TIME: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZoneName: "short",
};

export const SHORT_DATETIME: Intl.DateTimeFormatOptions = {
    ...SHORT_DATE,
    ...SHORT_TIME,
};

export const LONG_DATETIME: Intl.DateTimeFormatOptions = {
    ...LONG_DATE,
    ...SHORT_TIME,
};

/**
 * Dashboard formats: the SHORT_* options minus timeZoneName, because the
 * dashboard is always realm-local. The PHP half is App\Helpers\DateFormatHelper
 * and must be kept in step by hand.
 */
export const DASHBOARD_TIME: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
};

export const DASHBOARD_DATE: Intl.DateTimeFormatOptions = SHORT_DATE;

export const DASHBOARD_DATETIME: Intl.DateTimeFormatOptions = {
    ...SHORT_DATE,
    ...DASHBOARD_TIME,
};
